import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Globe2,
  History,
  Pencil,
  Play,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import Card from './Card';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import {
  schedulerApi,
  type JobRunHistory,
  type ScheduleInput,
  type SchedulerJob,
  type SchedulerJobType,
  type SchedulerUser,
} from '../api/scheduler';
import { formatDateTime, timeAgoWithDate } from '../utils/formatTime';

const STATUS_STYLES: Record<string, { color: string; icon: typeof CheckCircle; label: string }> = {
  ok: { color: 'text-green-600 dark:text-green-400', icon: CheckCircle, label: 'OK' },
  error: { color: 'text-red-600 dark:text-red-400', icon: XCircle, label: 'Error' },
  never_run: { color: 'text-slate-400', icon: Clock, label: 'Never Run' },
};

const DEFAULT_FORM: ScheduleInput = {
  name: '',
  job_type: 'daily_command_brief',
  user_id: null,
  cron_expression: '0 6 * * *',
  timezone: 'America/New_York',
  is_enabled: true,
};

const CRON_PRESETS = [
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 2 hours', value: '0 */2 * * *' },
  { label: 'Every 4 hours', value: '0 */4 * * *' },
  { label: 'Daily at 2:00 AM', value: '0 2 * * *' },
  { label: 'Daily at 6:00 AM', value: '0 6 * * *' },
  { label: 'Daily at 9:00 AM', value: '0 9 * * *' },
  { label: 'Monday at 9:00 AM', value: '0 9 * * 1' },
];

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.never_run;
  const Icon = style.icon;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${style.color}`}>
      <Icon size={12} />
      {style.label}
    </span>
  );
}

function formatNextRun(iso: string | null): string {
  if (!iso) return 'No upcoming run';
  const date = new Date(iso);
  const diffMs = date.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const diffHr = Math.round(diffMin / 60);
  if (diffMs < 0) return 'Overdue';
  if (diffMin < 60) return `in ${Math.max(1, diffMin)}m`;
  if (diffHr < 24) return `in ${diffHr}h`;
  return formatDateTime(iso);
}

function errorText(error: any, fallback: string): string {
  return error?.response?.data?.detail || error?.message || fallback;
}

export default function SchedulerPanel() {
  const [jobs, setJobs] = useState<SchedulerJob[]>([]);
  const [users, setUsers] = useState<SchedulerUser[]>([]);
  const [jobTypes, setJobTypes] = useState<SchedulerJobType[]>([]);
  const [filter, setFilter] = useState('all');
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [triggering, setTriggering] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, JobRunHistory[]>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [editing, setEditing] = useState<SchedulerJob | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ScheduleInput>(DEFAULT_FORM);

  const statusParams = useMemo(() => {
    if (filter === 'global') return { scope: 'global' as const };
    if (filter !== 'all') return { user_id: filter };
    return { scope: 'all' as const };
  }, [filter]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [status, userRows, typeRows] = await Promise.all([
        schedulerApi.getStatus(statusParams),
        schedulerApi.getUsers(),
        schedulerApi.getJobTypes(),
      ]);
      setJobs(status.jobs);
      setRunning(status.scheduler_running);
      setUsers(userRows);
      setJobTypes(typeRows);
    } catch (e: any) {
      setError(errorText(e, 'Failed to load scheduler management'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [statusParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => load(true), 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const openCreate = () => {
    const firstType = jobTypes[0];
    const selectedUserId = filter !== 'all' && filter !== 'global' ? filter : null;
    setEditing(null);
    setForm({
      ...DEFAULT_FORM,
      job_type: firstType?.job_type || DEFAULT_FORM.job_type,
      name: firstType?.label || 'New Schedule',
      cron_expression: firstType?.default_cron || DEFAULT_FORM.cron_expression,
      user_id: selectedUserId,
    });
    setError('');
    setShowForm(true);
  };

  const openEdit = (job: SchedulerJob) => {
    setEditing(job);
    setForm({
      name: job.name,
      job_type: job.job_type,
      user_id: job.user_id,
      cron_expression: job.cron_expression,
      timezone: job.timezone,
      is_enabled: job.is_enabled,
    });
    setError('');
    setShowForm(true);
  };

  const changeJobType = (jobType: string) => {
    const selected = jobTypes.find(item => item.job_type === jobType);
    setForm(prev => ({
      ...prev,
      job_type: jobType,
      name: editing ? prev.name : (selected?.label || prev.name),
      cron_expression: editing ? prev.cron_expression : (selected?.default_cron || prev.cron_expression),
    }));
  };

  const saveSchedule = async () => {
    if (!form.name.trim() || !form.cron_expression.trim()) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editing) {
        await schedulerApi.updateSchedule(editing.schedule_id, form);
        setSuccess(`Updated ${form.name}`);
      } else {
        await schedulerApi.createSchedule(form);
        setSuccess(`Created ${form.name}`);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(errorText(e, 'Failed to save schedule'));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (job: SchedulerJob) => {
    setError('');
    setSuccess('');
    try {
      await schedulerApi.updateSchedule(job.schedule_id, { is_enabled: !job.is_enabled });
      setSuccess(`${job.name} ${job.is_enabled ? 'disabled' : 'enabled'}`);
      await load();
    } catch (e: any) {
      setError(errorText(e, 'Failed to change schedule state'));
    }
  };

  const deleteSchedule = async (job: SchedulerJob) => {
    if (!window.confirm(`Delete "${job.name}"? Future runs will stop. Existing run history will be retained.`)) return;
    setError('');
    setSuccess('');
    try {
      await schedulerApi.deleteSchedule(job.schedule_id);
      setSuccess(`Deleted ${job.name}`);
      setExpandedJob(null);
      await load();
    } catch (e: any) {
      setError(errorText(e, 'Failed to delete schedule'));
    }
  };

  const triggerJob = async (job: SchedulerJob) => {
    setTriggering(job.schedule_id);
    setError('');
    setSuccess('');
    try {
      await schedulerApi.triggerJob(job.schedule_id);
      setSuccess(`Started ${job.name}`);
      setHistory(prev => {
        const next = { ...prev };
        delete next[job.schedule_id];
        return next;
      });
      window.setTimeout(() => load(true), 2000);
    } catch (e: any) {
      setError(errorText(e, 'Trigger failed'));
    } finally {
      setTriggering(null);
    }
  };

  const toggleHistory = async (scheduleId: string) => {
    if (expandedJob === scheduleId) {
      setExpandedJob(null);
      return;
    }
    setExpandedJob(scheduleId);
    if (!history[scheduleId]) {
      setHistoryLoading(scheduleId);
      try {
        const rows = await schedulerApi.getHistory(scheduleId, 10);
        setHistory(prev => ({ ...prev, [scheduleId]: rows }));
      } catch (e: any) {
        setError(errorText(e, 'Failed to load execution history'));
      } finally {
        setHistoryLoading(null);
      }
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex h-36 items-center justify-center">
          <LoadingSpinner size="md" label="Loading scheduler management..." />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Clock size={20} className="text-brand-500" />
            <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100">Scheduler</h2>
            {running ? (
              <span className="flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                <Activity size={10} className="animate-pulse" />Running
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                <AlertCircle size={10} />Stopped
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Schedules for system-wide jobs <span className="text-slate-400">(scope: All applicable users)</span> and for
            individual users. Use the filter to view system jobs or one user's schedules.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => load()} size="sm" disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />Refresh
          </Button>
          <Button onClick={openCreate} size="sm"><Plus size={14} />New Schedule</Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{error}</div>
      )}
      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">{success}</div>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">View schedules for</span>
          <select
            value={filter}
            onChange={event => setFilter(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="all">All schedules (system + users)</option>
            <option value="global">System-wide schedules</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name || user.email} ({user.schedule_count}){user.is_active ? '' : ' — inactive'}
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-2 text-xs text-slate-500">
          <span className="rounded-lg bg-slate-100 px-2.5 py-2 dark:bg-slate-800">{jobs.length} shown</span>
          <span className="rounded-lg bg-slate-100 px-2.5 py-2 dark:bg-slate-800">{users.length} users</span>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center dark:border-slate-700">
          <Calendar className="mx-auto text-slate-400" size={28} />
          <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-300">No schedules in this view</p>
          <button onClick={openCreate} className="mt-1 text-sm font-medium text-brand-600 hover:text-brand-500">Create one now</button>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => (
            <div key={job.schedule_id} className={`overflow-hidden rounded-lg border ${job.is_enabled ? 'border-slate-200 dark:border-slate-700' : 'border-slate-200 bg-slate-50/70 opacity-75 dark:border-slate-700 dark:bg-slate-900/30'}`}>
              <div className="flex flex-col gap-3 p-3 xl:flex-row xl:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <button onClick={() => toggleHistory(job.schedule_id)} className="mt-0.5 shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" aria-label="Toggle execution history">
                    {expandedJob === job.schedule_id ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{job.name}</p>
                      <span className="rounded-md bg-brand-500/10 px-1.5 py-0.5 text-[11px] font-medium text-brand-700 dark:text-brand-300">{job.job_type_label}</span>
                      {!job.is_enabled && <span className="rounded-md bg-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">DISABLED</span>}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        {job.user_id ? <User size={12} /> : <Globe2 size={12} />}
                        {job.user_id ? (job.user_name || job.user_email) : 'All applicable users'}
                      </span>
                      <span className="font-mono">{job.cron_expression}</span>
                      <span>{job.timezone}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} />Next: {formatNextRun(job.next_run)}</span>
                      {job.last_started && <span>Last: {timeAgoWithDate(job.last_started)}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pl-7 xl:pl-0">
                  <StatusBadge status={job.last_status} />
                  <Button variant="secondary" size="sm" onClick={() => triggerJob(job)} disabled={triggering === job.schedule_id} title="Run now">
                    {triggering === job.schedule_id ? <LoadingSpinner size="sm" /> : <Play size={12} />}Run
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleEnabled(job)} title={job.is_enabled ? 'Disable schedule' : 'Enable schedule'}>
                    <Power size={13} />{job.is_enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(job)} title="Edit schedule"><Pencil size={13} />Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteSchedule(job)} className="text-red-600 hover:text-red-700" title="Delete schedule"><Trash2 size={13} />Delete</Button>
                </div>
              </div>

              {job.last_error && job.last_status === 'error' && (
                <div className="px-3 pb-3 pl-10">
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 font-mono text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{job.last_error}</div>
                </div>
              )}

              {expandedJob === job.schedule_id && (
                <div className="border-t border-slate-200 bg-slate-50/50 dark:border-slate-700 dark:bg-slate-800/20">
                  {historyLoading === job.schedule_id ? (
                    <div className="flex justify-center py-5"><LoadingSpinner size="sm" label="Loading history..." /></div>
                  ) : history[job.schedule_id]?.length ? (
                    <div className="space-y-1.5 p-3">
                      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-slate-500"><History size={12} />Recent executions</div>
                      {history[job.schedule_id].map(run => (
                        <div key={run.id} className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-900">
                          <StatusBadge status={run.status} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 text-slate-500">
                              <span>{formatDateTime(run.started_at)}</span>
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{run.trigger_source}</span>
                              {run.finished_at && <span>({((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000).toFixed(1)}s)</span>}
                            </div>
                            {run.error_message && <p className="mt-1 break-all font-mono text-red-600 dark:text-red-400">{run.error_message}</p>}
                            {run.result_summary && <p className="mt-0.5 truncate text-slate-500 dark:text-slate-400">{run.result_summary}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-xs text-slate-400">No execution history yet</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-400 dark:border-slate-700">
        Standard five-field cron: minute, hour, day, month, weekday. Only approved dashboard operations can be scheduled; arbitrary commands are blocked.
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Schedule' : 'New Schedule'} size="lg">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Schedule name</span>
              <input value={form.name} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} maxLength={120} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" placeholder="Daily opportunity scan" />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Operation</span>
              <select value={form.job_type} onChange={event => changeJobType(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                {jobTypes.map(item => <option key={item.job_type} value={item.job_type}>{item.label}</option>)}
              </select>
              <span className="mt-1 block text-xs text-slate-500">{jobTypes.find(item => item.job_type === form.job_type)?.description}</span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Run for</span>
              <select value={form.user_id || ''} onChange={event => setForm(prev => ({ ...prev, user_id: event.target.value || null }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                <option value="">All applicable users (system-wide)</option>
                {users.map(user => <option key={user.id} value={user.id}>{user.name || user.email}{user.is_active ? '' : ' — inactive'}</option>)}
              </select>
              <span className="mt-1 block text-xs text-slate-500">Choose one customer to isolate the job to that account.</span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Cron expression</span>
              <input value={form.cron_expression} onChange={event => setForm(prev => ({ ...prev, cron_expression: event.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-mono text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" placeholder="0 9 * * *" />
              <span className="mt-1 block text-xs text-slate-500">minute hour day month weekday</span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Preset</span>
              <select value={CRON_PRESETS.some(item => item.value === form.cron_expression) ? form.cron_expression : ''} onChange={event => event.target.value && setForm(prev => ({ ...prev, cron_expression: event.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                <option value="">Custom expression</option>
                {CRON_PRESETS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>

            <label className="block sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Timezone</span>
              <input list="scheduler-timezones" value={form.timezone} onChange={event => setForm(prev => ({ ...prev, timezone: event.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" />
              <datalist id="scheduler-timezones">
                <option value="America/New_York" />
                <option value="America/Chicago" />
                <option value="America/Denver" />
                <option value="America/Los_Angeles" />
                <option value="Europe/Berlin" />
                <option value="UTC" />
              </datalist>
            </label>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <span>
              <span className="block text-sm font-medium text-slate-800 dark:text-slate-200">Schedule enabled</span>
              <span className="block text-xs text-slate-500">Disabled schedules stay saved but do not run automatically.</span>
            </span>
            <input type="checkbox" checked={form.is_enabled} onChange={event => setForm(prev => ({ ...prev, is_enabled: event.target.checked }))} className="h-5 w-5 rounded border-slate-300 text-brand-600" />
          </label>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveSchedule} disabled={saving || !form.name.trim() || !form.cron_expression.trim()}>
              {saving ? <LoadingSpinner size="sm" /> : editing ? <Pencil size={14} /> : <Plus size={14} />}
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Schedule'}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
